import React, { useState, useEffect, useRef, useContext } from 'react';
import { Lesson, Stats, KeystrokeEvent } from '../../types';
import StatBox from '../ui/StatBox';
import HandGuideKeyboard from '../ui/HandGuideKeyboard';
import TutorialHelper from '../ui/TutorialHelper';
import LessonCharacterQueue from './LessonCharacterQueue';
import { playCorrectSound, playWrongSound } from '../../utils/audio';
import { AccessibilityContext } from '../../App';
import Confetti from '../ui/Confetti';
import { ScorePops, useScorePops } from '../ui/ScorePops';
import { StarRating } from '../games/GameShell';

// Shown every 10 correct keys in a row.
const CHEERS = [
  { text: 'Great! 🎉', color: '#16a34a' },
  { text: 'Awesome! 🌟', color: '#2563eb' },
  { text: 'Super! 🚀', color: '#c026d3' },
  { text: 'On fire! 🔥', color: '#ea580c' },
  { text: 'Amazing! 🦄', color: '#7c3aed' },
];

interface TypingTutorProps {
  lesson: Lesson;
  onLessonComplete: (lessonId: string | number, stats: Omit<Stats, 'errors'>, errors?: Array<{ expected: string, typed: string }>, keystrokeLog?: KeystrokeEvent[]) => void;
  onExit: () => void;
  onStartNextLesson?: () => void;
}

const TypingTutor: React.FC<TypingTutorProps> = ({ lesson, onLessonComplete, onExit, onStartNextLesson }) => {
  const [typedText, setTypedText] = useState("");
  const [startTime, setStartTime] = useState<number | null>(null);
  const [stats, setStats] = useState<Stats>({ wpm: 0, acc: 100, errors: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const [keystrokeLog, setKeystrokeLog] = useState<KeystrokeEvent[]>([]);
  const [streak, setStreak] = useState(0);
  const [shake, setShake] = useState(false);
  const streakRef = useRef(0);
  const { pops, pop } = useScorePops();
  const inputRef = useRef<HTMLInputElement>(null);

  const accessibilityContext = useContext(AccessibilityContext);
  if (!accessibilityContext) {
    throw new Error("Accessibility context must be used within a provider.");
  }
  const { isVoiceOver } = accessibilityContext;

  // Sanitize lesson text to prevent issues with invisible characters like carriage returns or non-breaking spaces.
  const lessonText = (lesson.lesson_text || "").replace(/\r/g, "").replace(/\u00A0/g, " ");
  const isTutorial = typeof lesson.lesson_id === 'string' && lesson.lesson_id.startsWith('tutorial-');

  useEffect(() => {
    setTypedText("");
    setStartTime(null);
    setStats({ wpm: 0, acc: 100, errors: 0 });
    setIsFinished(false);
    setKeystrokeLog([]);
    streakRef.current = 0;
    setStreak(0);
    // Deferred: the input is still `disabled` (tied to the previous isFinished
    // value) in the DOM at this point, since this render hasn't committed yet --
    // focusing now would silently fail and leave the student unable to type
    // without clicking first.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [lesson]);

  // Auto-advance to the next lesson a few seconds after finishing, so students
  // don't have to click through manually between lessons.
  useEffect(() => {
    if (!isFinished || !onStartNextLesson) return;
    const timer = setTimeout(() => onStartNextLesson(), 3000);
    return () => clearTimeout(timer);
  }, [isFinished, onStartNextLesson]);
  
  // This effect handles all keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFinished || !inputRef.current || !lessonText || typedText.length >= lessonText.length) return;

      const eventTime = startTime ? Date.now() - startTime : 0;

      if (e.key.length === 1 || e.key === ' ' || e.key === 'Backspace') {
          setPressedKey(e.key);
          setTimeout(() => setPressedKey(null), 100);
      }
      
      if (e.key === 'Backspace') {
          e.preventDefault();
          if (typedText.length === 0) return;
          setKeystrokeLog(prev => [...prev, { key: 'Backspace', time: eventTime, type: 'backspace' }]);
          setTypedText(prev => prev.slice(0, -1));
          return;
      }
      
      if (e.key.length === 1) { // Includes spacebar
          e.preventDefault();
          
          if (!startTime) {
              setStartTime(Date.now());
          }

          const expectedChar = lessonText[typedText.length];
          const isCorrect = e.key === expectedChar;
          
          setKeystrokeLog(prev => [...prev, { key: e.key, time: eventTime, type: isCorrect ? 'correct' : 'incorrect' }]);
          
          if(isCorrect) {
              playCorrectSound();
              streakRef.current++;
              setStreak(streakRef.current);
              if (streakRef.current % 10 === 0) {
                  const cheer = CHEERS[(streakRef.current / 10 - 1) % CHEERS.length];
                  pop(cheer.text, 50, 10, cheer.color);
              }
              if (isVoiceOver && e.key === ' ') {
                  const lastSpaceIndex = typedText.lastIndexOf(' ');
                  const wordToSpeak = typedText.substring(lastSpaceIndex + 1);
                  if (wordToSpeak.trim().length > 0) {
                      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
                      window.speechSynthesis.speak(utterance);
                  }
              }
          } else {
              playWrongSound();
              streakRef.current = 0;
              setStreak(0);
              setShake(true);
              setTimeout(() => setShake(false), 400);
          }

          setTypedText(prev => prev + e.key);
      }
    };

    const inputEl = inputRef.current;
    inputEl?.addEventListener('keydown', handleKeyDown);
    return () => inputEl?.removeEventListener('keydown', handleKeyDown);
  }, [typedText, isFinished, lessonText, startTime, isVoiceOver, pop]);

  // This effect calculates stats and checks for lesson completion
  useEffect(() => {
      // Final stats are locked in when the lesson finishes; re-running later would keep shrinking WPM.
      if (!startTime || isFinished) return;

      const correctChars = typedText.split('').reduce((acc, char, i) => {
          return acc + (lessonText[i] === char ? 1 : 0);
      }, 0);

      const errorCount = typedText.length - correctChars;
      const elapsedMinutes = (Date.now() - startTime) / 60000;

      const wpm = elapsedMinutes > 0 ? Math.round((correctChars / 5) / elapsedMinutes) : 0;
      const acc = typedText.length > 0 ? Math.round((correctChars / typedText.length) * 100) : 100;

      setStats({ wpm, acc, errors: errorCount });
      
      if (typedText.length >= lessonText.length && !isFinished) {
          setIsFinished(true);
          const finalErrorLog: Array<{ expected: string, typed: string }> = [];
          for (let i = 0; i < lessonText.length; i++) {
              if (typedText[i] !== lessonText[i]) {
                  finalErrorLog.push({ expected: lessonText[i], typed: typedText[i] || "" });
              }
          }
          onLessonComplete(lesson.lesson_id, { wpm, acc }, finalErrorLog, keystrokeLog);
      }
  }, [typedText, startTime, lessonText, lesson.lesson_id, onLessonComplete, isFinished, keystrokeLog]);

  const currentCharIndex = typedText.length;
  const lessonProgressPct = lessonText.length > 0 ? Math.min(100, (currentCharIndex / lessonText.length) * 100) : 0;

  return (
    <div className="fun-frame relative overflow-hidden w-full max-w-6xl mx-auto p-4 md:p-6 rounded-3xl shadow-xl border-4 border-white bg-gradient-to-br from-sky-100 via-violet-50 to-pink-100" onClick={() => inputRef.current?.focus()}>
      <span className="fun-float absolute top-3 right-6 text-2xl opacity-40 pointer-events-none" aria-hidden="true">{'\u2B50'}</span>
      <span className="fun-float absolute top-24 left-3 text-xl opacity-30 pointer-events-none" style={{ animationDelay: '2s' }} aria-hidden="true">{'\u2728'}</span>
      <div className="relative flex flex-wrap items-center justify-between gap-2 mb-1">
        <h2 className="text-2xl md:text-3xl font-black flex items-center gap-3 min-w-0">
          <span className="fun-bounce inline-flex w-12 h-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-md text-2xl" aria-hidden="true">{'\uD83D\uDD24'}</span>
          <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 bg-clip-text text-transparent">{lesson.title}</span>
        </h2>
        {!isFinished && (
          <div className="flex items-center gap-2 text-xs md:text-sm font-black">
            {streak >= 5 && <span key={Math.floor(streak / 5)} className="fun-pop px-3 py-1 rounded-full bg-gradient-to-r from-orange-400 to-rose-500 text-white shadow ring-2 ring-white">{'\uD83D\uDD25'} {streak} in a row</span>}
            <span className="hidden sm:inline px-3 py-1 rounded-full bg-white text-sky-600 shadow-sm">{stats.wpm} WPM</span>
            <span className="hidden sm:inline px-3 py-1 rounded-full bg-white text-emerald-600 shadow-sm">{stats.acc}% ACC</span>
          </div>
        )}
      </div>
      {isTutorial && <TutorialHelper lessonId={lesson.lesson_id} />}

      <div className="relative my-5 mx-4" aria-hidden="true">
        <div className="h-4 bg-white rounded-full shadow-inner overflow-hidden">
          <div className="h-full bg-gradient-to-r from-pink-400 via-yellow-300 to-emerald-400 rounded-full transition-all duration-300" style={{ width: `${lessonProgressPct}%` }} />
        </div>
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-2xl transition-all duration-300 drop-shadow" style={{ left: `${lessonProgressPct}%` }}>{'\uD83D\uDE80'}</span>
        <span className="absolute top-1/2 -translate-y-1/2 -right-4 text-xl">{'\uD83C\uDFC1'}</span>
      </div>

      <div className={`relative p-4 md:p-6 mb-2 bg-white/70 rounded-2xl shadow-inner ${shake ? 'shake' : ''}`}>
        <LessonCharacterQueue lessonText={lessonText} typedText={typedText} />
        <ScorePops pops={pops} />
      </div>
      <input ref={inputRef} type="text" className="opacity-0 absolute w-0 h-0" aria-hidden="true" autoFocus disabled={isFinished} onBlur={({ target }) => target.focus()} />
      {/* Once finished, the celebration takes the keyboard's place so it's on screen without scrolling. */}
      {!isFinished && (
        <div className="bg-white/60 rounded-2xl px-2 pb-2">
          <HandGuideKeyboard nextChar={lessonText[currentCharIndex]} pressedKey={pressedKey} />
        </div>
      )}
      {isFinished && (() => {
        const stars = stats.acc >= 95 ? 3 : stats.acc >= 85 ? 2 : 1;
        return (
        <div className="relative mt-4 p-6 bg-white/80 border-4 border-white rounded-3xl shadow-md text-center">
          <Confetti />
          <h3 className="fun-pop text-4xl font-black bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-500 bg-clip-text text-transparent">Lesson Complete!</h3>
          <p className="text-lg font-semibold text-gray-600 mt-1">{stars === 3 ? 'Super-star typing! \uD83C\uDF1F' : stars === 2 ? 'Great job! \uD83D\uDC4F' : 'Nice work -- keep practicing! \uD83D\uDCAA'}</p>
          <StarRating stars={stars} />
          <div className="flex flex-wrap justify-center gap-4 my-5">
            {[{ label: 'WPM', value: stats.wpm }, { label: 'Accuracy', value: `${stats.acc}%` }, { label: 'Errors', value: stats.errors }].map((s, i) => (
              <div key={s.label} className="fun-pop" style={{ animationDelay: `${0.4 + i * 0.12}s` }}>
                <StatBox label={s.label} value={s.value} />
              </div>
            ))}
          </div>
          {onStartNextLesson && (
            <div className="max-w-xs mx-auto mb-2">
              <p className="text-sm font-bold text-violet-600 mb-1">Next lesson coming up...</p>
              <div className="h-2 bg-violet-100 rounded-full overflow-hidden">
                <div className="fun-drain h-full bg-gradient-to-r from-violet-400 to-fuchsia-400 rounded-full" />
              </div>
            </div>
          )}
          <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button
                  onClick={onExit}
                  className="px-6 py-2.5 bg-white text-gray-700 rounded-full font-black shadow-md ring-2 ring-gray-200 transition-transform hover:scale-105 active:scale-95">
                  {onStartNextLesson ? '\uD83D\uDDFA\uFE0F Back to Lessons' : 'Done'}
              </button>
              {onStartNextLesson && (
                  <button
                      onClick={onStartNextLesson}
                      className="fun-shine px-7 py-2.5 text-white rounded-full font-black shadow-lg ring-4 ring-white bg-gradient-to-r from-sky-500 to-violet-500 transition-transform hover:scale-105 active:scale-95">
                      {'Next Lesson \u25B6'}
                  </button>
              )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default TypingTutor;